import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '../../utils/idCodec';
import { NameCell } from '../../hooks/useNameMaps';
import { Alert, Button, Card, ConfirmDialog, SearchableSelect, StatusBadge, Tabs, useToast, type SearchableSelectOption } from '@kss/ui';
import { ChevronLeft, ChevronRight, Save, CheckCircle2, Truck, PackageCheck, XCircle, Plus, Trash2 } from 'lucide-react';
import type { SalesOrder, SalesOrderCreate, SalesOrderItem } from '@kss/api';
import { enumEntries, enumLabel, salesOrderService, storeService, customerService, productService } from '@kss/api';
import NumberField from '../../components/NumberField';
import { loadCustomerOptions, loadProductOptions } from '../shared/formLoaders';
import AddressFields from '../../components/AddressFields';
import { useModulePerms } from '../../hooks/useModulePerms';

type TabStatus = 'default' | 'danger' | 'success' | 'warning';
// Form state mirrors the live SalesOrder shape verified 2026-05-05.
// `channel` / `status` / `paymentStatus` are int enums on the backend;
// we keep human-readable strings in form state and convert at the
// save boundary via the maps below. `shippingAmount` and `isActive`
// were on the legacy DTO but are NOT on the live schema — POST
// rejects both with `UNKNOWN_FIELD`. Dropped from the form payload.

/* SSO Issue #4 (2026-07-16) — items[] grid added so sales orders
   actually carry line-item data. The backend cascades SalesOrderItem
   rows on save via SalesOrderService.MapHeaderWithItems (H1 fix
   shipped earlier this session) and decrements Stock.Quantity per
   line on AddAsync. Prior to this the form was header-only and every
   SO landed with zero SalesOrderItem rows and no stock movement.
   Mirrors the pattern in NewPurchaseOrder.tsx (SSO Issue #7). */
type LineRow = {
  key: string; // stable react key so removing a row doesn't reshuffle inputs
  productId: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
};

type FormState = {
  orderNumber: string;
  orderDate: string;
  channel: string;
  status: string;
  paymentStatus: string;
  customerId: string;
  storeId: string;
  warehouseId: string;
  subTotal: number;
  totalAmount: number;
  discountAmount: number;
  taxAmount: number;
  notes: string;
  items: LineRow[];
  /* Shipping address — serialised to `SalesOrder.shippingAddressJson`
     on save, parsed back on edit. PIN code drives city/state/country
     auto-fill via the shared `AddressFields` component (same UX as the
     storefront checkout). Picking a customer pre-fills line1 from the
     customer's `address` field if the address block is still blank.
     Bug ref: AI Agent 2026-05-26 — "existing customer address should be
     fetch and pincode enter auto search dropdown city/state/country
     auto fill add in all order pages". */
  shipFullName: string;
  shipPhone: string;
  shipLine1: string;
  shipLine2: string;
  shipPincode: string;
  shipCity: string;
  shipState: string;
  shipCountry: string;
};
const today = new Date().toISOString().slice(0, 10);
const newLineRow = (): LineRow => ({
  key: `l_${Math.random().toString(36).slice(2, 10)}`,
  productId: '',
  quantity: 1,
  unitPrice: 0,
  taxRate: 0,
});
const emptyForm: FormState = {
  orderNumber: '',
  orderDate: today,
  channel: 'POS',
  status: 'Draft',
  paymentStatus: 'Pending',
  customerId: '',
  storeId: '',
  warehouseId: '',
  subTotal: 0,
  totalAmount: 0,
  discountAmount: 0,
  taxAmount: 0,
  notes: '',
  shipFullName: '',
  shipPhone: '',
  shipLine1: '',
  shipLine2: '',
  shipPincode: '',
  shipCity: '',
  shipState: '',
  shipCountry: '91',
  items: [newLineRow()],
};

/** Line-level breakdown. Tax is derived from taxRate when > 0. */
function computeLineSubtotal(l: LineRow): number {
  return (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0);
}
function computeLineTax(l: LineRow): number {
  const sub = computeLineSubtotal(l);
  const rate = Number(l.taxRate) || 0;
  return rate > 0 ? (sub * rate) / 100 : 0;
}
function computeLineTotal(l: LineRow): number {
  return Math.round((computeLineSubtotal(l) + computeLineTax(l)) * 100) / 100;
}
function computeItemsTotals(items: LineRow[]): {
  subTotal: number;
  taxAmount: number;
  totalAmount: number;
} {
  let sub = 0;
  let tax = 0;
  for (const l of items) {
    sub += computeLineSubtotal(l);
    tax += computeLineTax(l);
  }
  return {
    subTotal: Math.round(sub * 100) / 100,
    taxAmount: Math.round(tax * 100) / 100,
    totalAmount: Math.round((sub + tax) * 100) / 100,
  };
}

const STEPS = ['order', 'customer', 'items', 'amounts', 'review'] as const;
type StepId = (typeof STEPS)[number];
const STEP_LABELS: Record<StepId, string> = {
  order: 'Order info', customer: 'Customer', items: 'Line items', amounts: 'Amounts', review: 'Review & save',
};

/* Single source of truth — see enums.ts SalesOrderChannelLabels
   (1=POS, 2=Online, 3=Web, 4=B2B, 5=B2C, 6=Marketplace; the storefront
   and PublicCheckoutController both write 3). */
const CHANNEL_BY_LABEL: Record<string, number> = Object.fromEntries(
  enumEntries('SalesOrderChannel').map((e) => [e.label, e.value]),
);
const LABEL_BY_CHANNEL: Record<number, string> = Object.fromEntries(
  Object.entries(CHANNEL_BY_LABEL).map(([k, v]) => [v, k]),
);

/* Single source of truth — see enums.ts SalesOrderStatusLabels. */
const STATUS_BY_LABEL: Record<string, number> = Object.fromEntries(
  enumEntries('SalesOrderStatus').map((e) => [e.label, e.value]),
);
const LABEL_BY_STATUS: Record<number, string> = Object.fromEntries(
  Object.entries(STATUS_BY_LABEL).map(([k, v]) => [v, k]),
);

/* Single source of truth — see enums.ts PaymentStatusLabels. */
const PAYMENT_STATUS_BY_LABEL: Record<string, number> = Object.fromEntries(
  enumEntries('PaymentStatus').map((e) => [e.label, e.value]),
);
const LABEL_BY_PAYMENT_STATUS: Record<number, string> = Object.fromEntries(
  Object.entries(PAYMENT_STATUS_BY_LABEL).map(([k, v]) => [v, k]),
);

const loadStoreOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await storeService.all();
  return (rows ?? []).map((s) => ({ value: s.storeId ?? '', label: s.name, description: s.code, disabled: !s.storeId }));
};

const inputCls = 'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls = 'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function SalesOrderForm() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const isEdit = !!id && id !== 'new';
  const perms = useModulePerms('SalesOrder');
  const canSave = isEdit ? perms.canUpdate : perms.canCreate;
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [step, setStep] = useState<StepId>('order');
  const [redTabs, setRedTabs] = useState<Set<StepId>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Raw backend record — held alongside `form` so the status-action bar
  // can PUT the untouched row plus a single overridden field without
  // depending on the human-readable form-state round-trip. `shippedAt`
  // in particular has no form input; keeping the record around means
  // status transitions preserve it (or set it) correctly.
  const [record, setRecord] = useState<SalesOrder | null>(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true); setLoadError(null);
        try {
          const rec = await salesOrderService.get(id);
          if (cancelled) return;
          setRecord(rec);
          /* Parse `shippingAddressJson` defensively — older orders
             may have written non-JSON free text, in which case we
             fall back to dropping the address into `shipLine1`. */
          const parsedAddr = parseShippingAddress(rec.shippingAddressJson);
          /* Hydrate items[] from the fetched SO. If the backend
             returned no items (legacy header-only rows), fall back to
             a single blank line so the grid isn't empty. */
          const hydratedItems: LineRow[] =
            (rec.items ?? []).length > 0
              ? (rec.items ?? []).map((i, idx): LineRow => ({
                  key: `edit_${i.salesOrderItemId ?? idx}`,
                  productId: i.productId ?? '',
                  quantity: Number(i.quantity ?? 0),
                  unitPrice: Number(i.unitPrice ?? 0),
                  taxRate: Number(i.taxRate ?? 0),
                }))
              : [newLineRow()];
          setForm({
            orderNumber: rec.orderNumber ?? '',
            orderDate: rec.orderDate ? rec.orderDate.slice(0, 10) : today,
            channel:
              typeof rec.channel === 'number'
                ? LABEL_BY_CHANNEL[rec.channel] ?? 'POS'
                : String(rec.channel ?? 'POS'),
            status:
              typeof rec.status === 'number'
                ? LABEL_BY_STATUS[rec.status] ?? 'Draft'
                : String(rec.status ?? 'Draft'),
            paymentStatus:
              typeof rec.paymentStatus === 'number'
                ? LABEL_BY_PAYMENT_STATUS[rec.paymentStatus] ?? 'Pending'
                : String(rec.paymentStatus ?? 'Pending'),
            customerId: rec.customerId ?? '',
            storeId: rec.storeId ?? '',
            warehouseId: rec.warehouseId ?? '',
            subTotal: Number(rec.subTotal ?? 0),
            totalAmount: Number(rec.totalAmount ?? 0),
            discountAmount: Number(rec.discountAmount ?? 0),
            taxAmount: Number(rec.taxAmount ?? 0),
            notes: rec.notes ?? '',
            shipFullName: parsedAddr.fullName,
            shipPhone: parsedAddr.phone,
            shipLine1: parsedAddr.line1,
            shipLine2: parsedAddr.line2,
            shipPincode: parsedAddr.pincode,
            shipCity: parsedAddr.city,
            shipState: parsedAddr.state,
            shipCountry: parsedAddr.country || '91',
            items: hydratedItems,
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setLoadError(msg); toastRef.current.error('Load failed', msg);
        } finally { if (!cancelled) setLoading(false); }
      } else setForm(emptyForm);
      setErrors({}); setStep('order'); setRedTabs(new Set());
    }
    run();
    return () => { cancelled = true; };
  }, [id, isEdit]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  /* Fetch the picked customer's master record and pre-fill the
     shipping form whenever the address block is still empty. We only
     read `Customer.address` (single free-text column on the live
     schema) plus `name` and `phone`. The address-book endpoint
     (`/auth/me/addresses`) is JWT-scoped so an admin can't call it
     for a different customerId — when a backend endpoint exposes
     CustomerAddress by id, swap the fetch here. See
     BACKEND_AGENT_PROMPTS.md "F-series" for the gap. */
  const prefilledFor = useRef<string>('');
  useEffect(() => {
    const cid = form.customerId;
    if (!cid || prefilledFor.current === cid) return;
    let cancelled = false;
    (async () => {
      try {
        const c = await customerService.get(cid);
        if (cancelled || !c) return;
        prefilledFor.current = cid;
        setForm((f) => ({
          ...f,
          // Only overwrite the blank fields so an admin's manual edits
          // aren't clobbered when they swap the customer.
          shipFullName: f.shipFullName || c.name || '',
          shipPhone: f.shipPhone || c.phone || '',
          shipLine1: f.shipLine1 || c.address || '',
        }));
      } catch {
        /* silent — admin can still type the address manually. */
      }
    })();
    return () => { cancelled = true; };
  }, [form.customerId]);
  const validateStep = (s: StepId) => {
    const errs: typeof errors = {};
    if (s === 'order' || s === 'review') { if (!form.orderNumber.trim()) errs.orderNumber = 'Order number is required'; if (!form.orderDate) errs.orderDate = 'Date is required'; }
    if (s === 'amounts' || s === 'review') if (Number(form.totalAmount) < 0) errs.totalAmount = 'Must be ≥ 0';
    if (s === 'items' || s === 'review') {
      const goodLines = form.items.filter((l) => l.productId && Number(l.quantity) > 0);
      if (goodLines.length === 0) {
        errs.items = 'Add at least one line item with a product and quantity > 0.';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  const goNext = () => {
    const i = STEPS.indexOf(step); if (i >= STEPS.length - 1) return;
    if (!validateStep(step)) { toast.error('Fix errors', 'Some fields are invalid'); return; }
    const next = STEPS[i + 1]; setRedTabs((p) => { const n = new Set(p); n.delete(next); return n; }); setStep(next);
  };
  const goPrev = () => { const i = STEPS.indexOf(step); if (i === 0) return; setRedTabs((p) => new Set(p).add(step)); setStep(STEPS[i - 1]); };
  const basicValid = !!form.orderNumber.trim() && !!form.orderDate;

  const handleSave = async () => {
    // Tab-wise: each tab only validates its own fields; Save & Next
    // on intermediate tabs just advances. API save runs on Review.
    if (!validateStep(step)) { toast.error('Fix errors', 'Some fields are invalid'); return; }
    const stepIdx = STEPS.indexOf(step);
    const isLastStep = stepIdx === STEPS.length - 1;
    if (!isLastStep) {
      const next = STEPS[stepIdx + 1];
      setRedTabs((p) => { const n = new Set(p); n.delete(next); return n; });
      setStep(next);
      return;
    }
    if (!validateStep('review')) {
      toast.error('Fix errors', 'Some fields are invalid — check the red tabs');
      return;
    }
    setSaving(true);
    try {
      // Live POST/PUT canonical schema (verified 2026-05-05): channel/
      // status/paymentStatus are int enums, no `shippingAmount` and no
      // `isActive` columns. Strict-mode binder rejects either with
      // `UNKNOWN_FIELD`. We translate human-readable labels to ints
      // here and drop the legacy fields from the wire payload.
      /* Serialise the shipping fields into the single
         `shippingAddressJson` column the live schema expects. Empty
         block → undefined so the wire payload stays clean. */
      const shipJson = serialiseShippingAddress({
        fullName: form.shipFullName,
        phone: form.shipPhone,
        line1: form.shipLine1,
        line2: form.shipLine2,
        pincode: form.shipPincode,
        city: form.shipCity,
        state: form.shipState,
        country: form.shipCountry,
      });
      /* Rebuild line items in wire shape. Server stamps
         salesOrderItemId / salesOrderId in MapHeaderWithItems (H1 fix
         earlier this session) — we send '' placeholders so the type
         stays satisfied. Discard blank rows (no product / zero qty)
         so the backend never receives an incomplete line. */
      const goodLines = form.items.filter((l) => l.productId && Number(l.quantity) > 0);
      const wireItems: SalesOrderItem[] = goodLines.map((l) => {
        const qty = Number(l.quantity);
        const unit = Number(l.unitPrice);
        const rate = Number(l.taxRate) || 0;
        const lineSub = qty * unit;
        const lineTax = rate > 0 ? (lineSub * rate) / 100 : 0;
        return {
          salesOrderId: id ?? '',
          productId: l.productId,
          quantity: qty,
          unitPrice: unit,
          taxRate: rate > 0 ? rate : undefined,
          taxAmount: rate > 0 ? Math.round(lineTax * 100) / 100 : undefined,
          total: Math.round((lineSub + lineTax) * 100) / 100,
        };
      });
      /* Auto-derive header amounts from items unless the user has
         manually typed a materially different total in the Amounts
         tab (mirrors NewPurchaseOrder.tsx behavior). Discount stays
         verbatim from the form — subTotal + tax - discount = total. */
      const derived = computeItemsTotals(form.items);
      const userSub = Number(form.subTotal) || 0;
      const userTax = Number(form.taxAmount) || 0;
      const userTotal = Number(form.totalAmount) || 0;
      const discount = Number(form.discountAmount) || 0;
      const subTotal =
        userSub > 0 && Math.abs(userSub - derived.subTotal) > 0.01 ? userSub : derived.subTotal;
      const taxAmount =
        userTax > 0 && Math.abs(userTax - derived.taxAmount) > 0.01 ? userTax : derived.taxAmount;
      const derivedTotal = Math.round((subTotal + taxAmount - discount) * 100) / 100;
      const totalAmount =
        userTotal > 0 && Math.abs(userTotal - derivedTotal) > 0.01 ? userTotal : derivedTotal;
      const body: SalesOrder = {
        salesOrderId: isEdit ? id : undefined,
        orderNumber: form.orderNumber,
        orderDate: form.orderDate,
        channel: CHANNEL_BY_LABEL[form.channel] ?? 1,
        status: STATUS_BY_LABEL[form.status] ?? 1,
        paymentStatus: PAYMENT_STATUS_BY_LABEL[form.paymentStatus] ?? 1,
        customerId: form.customerId || undefined,
        storeId: form.storeId || undefined,
        warehouseId: form.warehouseId || undefined,
        subTotal: subTotal || undefined,
        totalAmount,
        discountAmount: discount || undefined,
        taxAmount: taxAmount || undefined,
        notes: form.notes || undefined,
        shippingAddressJson: shipJson || undefined,
        items: wireItems,
      };
      if (isEdit) {
        await salesOrderService.update([body]);
        toast.success('Saved', `${form.orderNumber} updated`);
      } else {
        const { salesOrderId: _omit, ...create } = body;
        // Live `POST /api/SalesOrder` returns `data: true` instead of the
        // created entity — see BACKEND_AGENT_PROMPTS.md Item 4. Recover
        // the id by filtering on the unique `orderNumber`. Critically:
        // do NOT `return` when recovery fails, otherwise Save & Next is
        // swallowed and the user gets dumped at the list page.
        await salesOrderService.create([create as SalesOrderCreate]);
        toast.success('Saved', `${form.orderNumber} added`);
      }
      // Review-tab save succeeded — back to the list (matches edit mode).
      navigate('/panel/sales-orders');
    } catch (e) { toast.error('Save failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setSaving(false); }
  };

  /**
   * Advance / cancel the order without going through the full form
   * save. Uses the raw backend `record` as the payload base so
   * fields the form doesn't expose (shippedAt, createdAt, tenantId,
   * …) round-trip untouched. On success we refetch so the visible
   * status pill and enabled action buttons stay in sync with the
   * backend view.
   */
  const changeStatus = async (next: number) => {
    if (!isEdit || !id || !record) return;
    setStatusBusy(true);
    try {
      const payload: SalesOrder = { ...record, status: next };
      if (next === 3 && !record.shippedAt) {
        payload.shippedAt = new Date().toISOString();
      }
      await salesOrderService.update([payload]);
      toast.success('Order updated', `Status → ${enumLabel('SalesOrderStatus', next)}`);
      const refreshed = await salesOrderService.get(id);
      setRecord(refreshed);
      // Sync just the status pill in the visible form; leave the
      // other admin-edited fields alone in case they were mid-edit.
      const label =
        typeof refreshed.status === 'number'
          ? LABEL_BY_STATUS[refreshed.status] ?? 'Draft'
          : String(refreshed.status ?? 'Draft');
      setForm((f) => ({ ...f, status: label }));
    } catch (e) {
      toast.error('Update failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setStatusBusy(false);
    }
  };

  const tabStatus = useMemo<Record<string, TabStatus>>(() => { const m: Record<string, TabStatus> = {}; redTabs.forEach((s) => (m[s] = 'danger')); return m; }, [redTabs]);

  const field = (label: string, key: keyof FormState, input: React.ReactNode, required?: boolean) => (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-content-secondary">{label} {required && <span className="text-danger">*</span>}</span>
      {input}
      {errors[key] && <span className="text-xs text-danger">{errors[key]}</span>}
    </label>
  );

  const orderStep = (
    <div className="grid gap-3 md:grid-cols-2">
      {field('Order number', 'orderNumber', <input value={form.orderNumber} onChange={(e) => set('orderNumber', e.target.value)} placeholder="SO-1001" className={inputCls} />, true)}
      {field('Order date', 'orderDate', <input type="date" value={form.orderDate} onChange={(e) => set('orderDate', e.target.value)} className={inputCls} />, true)}
      {field('Channel', 'channel',
        <select value={form.channel} onChange={(e) => set('channel', e.target.value)} className={selectCls}>
          {Object.keys(CHANNEL_BY_LABEL).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>)}
      {field('Status', 'status',
        <select value={form.status} onChange={(e) => set('status', e.target.value)} className={selectCls}>
          {Object.keys(STATUS_BY_LABEL).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>)}
      {field('Payment status', 'paymentStatus',
        <select value={form.paymentStatus} onChange={(e) => set('paymentStatus', e.target.value)} className={selectCls}>
          {Object.keys(PAYMENT_STATUS_BY_LABEL).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>)}
    </div>
  );
  const customerStep = (
    <div className="grid gap-3">
      <div className="grid gap-3 md:grid-cols-2">
        {/* Customer picker — backed by GET /api/Customer/all (live
            since 2026-05-02). Each option shows name + (email / phone
            / code) as a description so duplicates resolve cleanly. */}
        <div className="flex flex-col gap-1">
          <SearchableSelect
            label="Customer"
            value={form.customerId}
            onChange={(v) => set('customerId', v)}
            loadOptions={loadCustomerOptions}
            placeholder="Pick a customer"
            searchPlaceholder="Search customers…"
          />
          <p className="text-[11px] text-content-muted">
            Source: <code className="font-mono">/api/Customer/all</code> — add or edit customers under <strong>Sidebar → Customers</strong>.
          </p>
        </div>
        <SearchableSelect label="Store" value={form.storeId} onChange={(v) => set('storeId', v)} loadOptions={loadStoreOptions} placeholder="Select a store" searchPlaceholder="Search stores..." />
      </div>
      {/* Customer-history hint: shows the picked customer's three
          most-recent orders so the admin has context (typical
          channel, last order date, average order value) without
          leaving this page. */}
      <CustomerOrdersHint customerId={form.customerId} />

      {/* ── Shipping address ──────────────────────────────────────
          Auto-prefilled from the picked customer's master record
          (name / phone / address). Pincode → city / state / country
          via the shared `AddressFields` component, same UX as the
          storefront checkout. Persisted as `shippingAddressJson`. */}
      <div className="mt-2 rounded-lg border border-border bg-surface-card p-4">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-content-secondary">
          Shipping address
        </h3>
        <div className="grid gap-3 md:grid-cols-2">
          {field('Full name', 'shipFullName',
            <input value={form.shipFullName} onChange={(e) => set('shipFullName', e.target.value)} placeholder="Customer name" className={inputCls} autoComplete="name" />,
          )}
          {field('Phone', 'shipPhone',
            <input value={form.shipPhone} onChange={(e) => set('shipPhone', e.target.value.replace(/\D+/g, '').slice(0, 15))} placeholder="10-digit mobile" maxLength={15} inputMode="numeric" pattern="\d{10,15}" className={inputCls} autoComplete="tel" />,
          )}
          <AddressFields
            addressLine1={form.shipLine1}
            setAddressLine1={(v) => set('shipLine1', v)}
            addressLine2={form.shipLine2}
            setAddressLine2={(v) => set('shipLine2', v)}
            showLine2
            pincode={form.shipPincode}
            setPincode={(v) => set('shipPincode', v)}
            city={form.shipCity}
            setCity={(v) => set('shipCity', v)}
            state={form.shipState}
            setState={(v) => set('shipState', v)}
            country={form.shipCountry}
            setCountry={(v) => set('shipCountry', v)}
          />
        </div>
        <p className="mt-2 text-[11px] text-content-muted">
          Tip: type the 6-digit PIN code first — city, state and country auto-fill.
        </p>
      </div>
    </div>
  );
  const itemsDerived = useMemo(() => computeItemsTotals(form.items), [form.items]);
  const itemsStep = (
    <div className="grid gap-3">
      <ItemsGrid value={form.items} onChange={(next) => set('items', next)} />
      {errors.items && <span className="text-xs text-danger">{errors.items}</span>}
      <p className="text-[11px] text-content-muted">
        The header amounts (Subtotal / Tax / Total) below are auto-derived from these rows on save.
        Backend re-runs the same math server-side (SalesOrderService.MapHeaderWithItems). Stock is
        decremented per line on save.
      </p>
    </div>
  );
  const amountsStep = (
    <div className="grid gap-3">
      <div className="rounded-md border border-border bg-surface-card-hover p-3 text-xs text-content-muted">
        Auto-derived from line items:&nbsp;
        <strong className="text-content-primary">Subtotal ₹ {itemsDerived.subTotal.toFixed(2)}</strong>
        <span className="mx-1">·</span>
        <strong className="text-content-primary">Tax ₹ {itemsDerived.taxAmount.toFixed(2)}</strong>
        <span className="mx-1">·</span>
        <strong className="text-content-primary">Total ₹ {itemsDerived.totalAmount.toFixed(2)}</strong>
        <span className="ml-2">— override any field below to disable auto-fill for that field on save.</span>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {field('Subtotal (₹)', 'subTotal', <NumberField min={0} step="0.01" value={form.subTotal} onValueChange={(n) => set('subTotal', n)} className={inputCls} />)}
        {field('Total (₹)', 'totalAmount', <NumberField min={0} step="0.01" value={form.totalAmount} onValueChange={(n) => set('totalAmount', n)} className={inputCls} />, true)}
        {field('Discount (₹)', 'discountAmount', <NumberField min={0} step="0.01" value={form.discountAmount} onValueChange={(n) => set('discountAmount', n)} className={inputCls} />)}
        {field('Tax (₹)', 'taxAmount', <NumberField min={0} step="0.01" value={form.taxAmount} onValueChange={(n) => set('taxAmount', n)} className={inputCls} />)}
        {field('Notes', 'notes', <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} className={`${inputCls} h-auto py-2`} placeholder="Optional notes..." />)}
      </div>
    </div>
  );
  const goodLineCount = form.items.filter((l) => l.productId && Number(l.quantity) > 0).length;
  const reviewStep = (
    <div className="grid gap-3">
      <div className="flex items-center gap-2 text-success"><CheckCircle2 className="h-4 w-4" /><span className="text-sm font-medium">Ready to save</span></div>
      <div className="rounded-md border border-border bg-surface-card-hover p-4 text-sm">
        <div className="grid gap-2 md:grid-cols-2">
          <Row label="Order #" value={form.orderNumber} />
          <Row label="Date" value={form.orderDate} />
          <Row label="Channel" value={form.channel} />
          <Row label="Status" value={form.status} />
          <Row label="Payment status" value={form.paymentStatus} />
          <Row label="Customer" value={<NameCell kind="customer" id={form.customerId} />} />
          <Row label="Store" value={<NameCell kind="store" id={form.storeId} />} />
          <Row label="Line items" value={`${goodLineCount} — total ₹ ${itemsDerived.totalAmount.toFixed(2)}`} />
          <Row label="Total" value={`₹ ${Number(form.totalAmount || itemsDerived.totalAmount).toLocaleString('en-IN')}`} />
        </div>
      </div>
      {errors.items && <span className="text-xs text-danger">{errors.items}</span>}
    </div>
  );

  const tabs = [
    { id: 'order' as const, label: STEP_LABELS.order, content: orderStep },
    { id: 'customer' as const, label: STEP_LABELS.customer, content: customerStep },
    { id: 'items' as const, label: STEP_LABELS.items, content: itemsStep },
    { id: 'amounts' as const, label: STEP_LABELS.amounts, content: amountsStep },
    { id: 'review' as const, label: STEP_LABELS.review, content: reviewStep },
  ];
  const isFirst = STEPS.indexOf(step) === 0;
  const isLast = STEPS.indexOf(step) === STEPS.length - 1;

  // Status-action bar — only shown in edit mode. Mirrors the row
  // menu in AllSalesOrders so admins can advance the lifecycle from
  // either surface. Forward-only (Draft → Confirmed → Shipped →
  // Delivered); Cancel is gated by a confirm dialog and permitted
  // from any pre-Delivered state.
  const currentStatusInt = STATUS_BY_LABEL[form.status] ?? 0;
  const canConfirm = currentStatusInt === 1;
  const canShip = currentStatusInt === 2;
  const canDeliver = currentStatusInt === 3;
  const canCancel = currentStatusInt > 0 && currentStatusInt !== 4 && currentStatusInt !== 5;
  const showStatusBar = isEdit && !loading && !!record;

  return (
    <div className="-m-6 p-[3px]">
      {loadError && <Alert variant="danger" className="mb-3">{loadError}</Alert>}
      {showStatusBar && (
        <Card className="mb-[3px] flex flex-wrap items-center justify-between gap-3 p-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-content-secondary">Order status</span>
            <StatusBadge status={form.status} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canConfirm && (
              <Button size="sm" variant="primary" disabled={statusBusy} onClick={() => changeStatus(2)}>
                <CheckCircle2 className="h-4 w-4" /> Confirm
              </Button>
            )}
            {canShip && (
              <Button size="sm" variant="primary" disabled={statusBusy} onClick={() => changeStatus(3)}>
                <Truck className="h-4 w-4" /> Mark Shipped
              </Button>
            )}
            {canDeliver && (
              <Button size="sm" variant="primary" disabled={statusBusy} onClick={() => changeStatus(4)}>
                <PackageCheck className="h-4 w-4" /> Mark Delivered
              </Button>
            )}
            {canCancel && (
              <Button size="sm" variant="danger" disabled={statusBusy} onClick={() => setConfirmCancel(true)}>
                <XCircle className="h-4 w-4" /> Cancel Order
              </Button>
            )}
            {!canConfirm && !canShip && !canDeliver && !canCancel && (
              <span className="text-xs text-content-muted">No status transitions available.</span>
            )}
          </div>
        </Card>
      )}
      <div className="mb-[3px]">
        <Tabs tabs={tabs} activeId={step}
          onChange={(sid) => {
            const t = STEPS.indexOf(sid as StepId);
            const c = STEPS.indexOf(step);
            if (t < c) setRedTabs((p) => { const n = new Set(p); for (let i = t + 1; i <= c; i++) n.add(STEPS[i]); return n; });
            else if (t > c) setRedTabs((p) => { const n = new Set(p); for (let i = c + 1; i <= t; i++) n.delete(STEPS[i]); return n; });
            setStep(sid as StepId);
          }}
          tabStatus={tabStatus} barOnly />
      </div>
      <Card>
        <div className="min-h-[180px]">{loading ? <div className="text-sm text-content-muted">Loading…</div> : tabs.find((t) => t.id === step)?.content}</div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={goPrev} disabled={isFirst}><ChevronLeft className="h-4 w-4" /> Previous</Button>
            {!isLast && <Button type="button" onClick={goNext}>Next <ChevronRight className="h-4 w-4" /></Button>}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => navigate('/panel/sales-orders')}>Cancel</Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!basicValid || saving || (isLast && !canSave)}
              title={isLast && !canSave ? `You don't have permission to ${isEdit ? 'update' : 'create'} sales orders` : undefined}
            >
              <Save className="h-4 w-4" /> {saving ? 'Saving…' : isLast ? (isEdit ? 'Update' : 'Save') : 'Next →'}
            </Button>
          </div>
        </div>
      </Card>
      <ConfirmDialog
        isOpen={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={() => { setConfirmCancel(false); void changeStatus(5); }}
        title="Cancel Order?"
        message={`Cancel order ${form.orderNumber || ''}? The customer will no longer be able to modify it and the status will move to Cancelled.`}
        confirmLabel="Cancel Order"
        cancelLabel="Keep Order"
        variant="danger"
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Shipping-address (de)serialisation
   `SalesOrder.shippingAddressJson` is a single TEXT column on the
   live API. We keep an `AddressBlock` shape and round-trip it
   through JSON — older orders that wrote a plain string fall back
   to populating `line1`. */
interface AddressBlock {
  fullName: string;
  phone: string;
  line1: string;
  line2: string;
  pincode: string;
  city: string;
  state: string;
  country: string;
}
const EMPTY_ADDRESS: AddressBlock = {
  fullName: '', phone: '', line1: '', line2: '', pincode: '', city: '', state: '', country: '',
};
function parseShippingAddress(raw: string | null | undefined): AddressBlock {
  const v = String(raw ?? '').trim();
  if (!v) return EMPTY_ADDRESS;
  try {
    const obj = JSON.parse(v) as Record<string, unknown>;
    return {
      fullName: String(obj.fullName ?? obj.name ?? ''),
      phone: String(obj.phone ?? ''),
      line1: String(obj.line1 ?? obj.addressLine1 ?? obj.address ?? ''),
      line2: String(obj.line2 ?? obj.addressLine2 ?? ''),
      pincode: String(obj.pincode ?? obj.postalCode ?? obj.pin ?? ''),
      city: String(obj.city ?? ''),
      state: String(obj.state ?? ''),
      country: String(obj.country ?? ''),
    };
  } catch {
    /* Old orders may have stored a free-text address — fall through
       so the admin sees it instead of an empty form. */
    return { ...EMPTY_ADDRESS, line1: v };
  }
}
function serialiseShippingAddress(a: AddressBlock): string {
  const allBlank = Object.values(a).every((v) => !String(v ?? '').trim());
  if (allBlank) return '';
  return JSON.stringify(a);
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">{value !== '' && value != null ? value : <em className="text-content-muted">—</em>}</span>
    </div>
  );
}

/**
 * Editable line-item grid for the Sales Order form. Rows drive the
 * SalesOrderItem table on save via SalesOrderService.MapHeaderWithItems.
 * On picking a product we auto-fill unitPrice from `sellingPrice` and
 * taxRate from `taxRate` when the row is still at its defaults. The
 * running grand total in the tfoot mirrors what the backend will
 * compute so the admin sees the number they're about to send.
 *
 * Mirrors NewPurchaseOrder.tsx's ItemsGrid (SSO Issue #7); the only
 * shape delta is `unitCost` → `unitPrice`.
 */
function ItemsGrid({
  value,
  onChange,
}: {
  value: LineRow[];
  onChange: (next: LineRow[]) => void;
}) {
  /* `productOptionsLoading` gates the trigger label so the row-level
     SearchableSelect shows "Loading…" for an edit-hydrated productId
     while the options list is still in flight. Without it the picker
     briefly flashed the "Pick a product" placeholder for a row that
     already had a persisted product — same regression the Purchase
     Order form's grid hit. */
  const [productOptions, setProductOptions] = useState<SearchableSelectOption[]>([]);
  const [productOptionsLoading, setProductOptionsLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setProductOptionsLoading(true);
    loadProductOptions()
      .then((opts) => {
        if (!cancelled) setProductOptions(opts);
      })
      .catch(() => {
        /* swallow — the picker just renders an empty list and error
           surfacing is handled by the toast layer at save. */
      })
      .finally(() => {
        if (!cancelled) setProductOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* Price + tax cache keyed by productId. Populated once per grid
     mount so we can auto-fill unitPrice / taxRate the first time a
     product is picked into a fresh row. */
  const [productPriceCache, setProductPriceCache] = useState<
    Record<string, { sellingPrice: number; taxRate: number }>
  >({});
  useEffect(() => {
    if (productOptions.length === 0) return;
    let cancelled = false;
    productService
      .all()
      .then((rows) => {
        if (cancelled) return;
        const map: Record<string, { sellingPrice: number; taxRate: number }> = {};
        for (const r of rows ?? []) {
          const pid = (r as { productId?: string }).productId;
          if (!pid) continue;
          const sp = (r as { sellingPrice?: number }).sellingPrice;
          const price = (r as { price?: number }).price;
          const tr = (r as { taxRate?: number }).taxRate;
          map[pid] = {
            sellingPrice: Number(sp ?? price ?? 0),
            taxRate: Number(tr ?? 0),
          };
        }
        setProductPriceCache(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [productOptions.length]);

  const patchRow = (idx: number, patch: Partial<LineRow>) => {
    const next = value.slice();
    next[idx] = { ...next[idx], ...patch };
    /* Auto-fill unitPrice and taxRate on product pick when the row is
       still at its defaults. We only touch a field if it's zero so
       an admin's manual edits aren't clobbered when they swap the
       product. */
    if (patch.productId) {
      const cached = productPriceCache[patch.productId];
      if (cached) {
        if ((next[idx].unitPrice === 0 || Number.isNaN(next[idx].unitPrice)) && cached.sellingPrice > 0) {
          next[idx].unitPrice = cached.sellingPrice;
        }
        if ((next[idx].taxRate === 0 || Number.isNaN(next[idx].taxRate)) && cached.taxRate > 0) {
          next[idx].taxRate = cached.taxRate;
        }
      }
    }
    onChange(next);
  };
  const addRow = () => onChange([...value, newLineRow()]);
  const removeRow = (idx: number) => {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next.length === 0 ? [newLineRow()] : next);
  };

  const grandTotal = value.reduce((acc, l) => acc + computeLineTotal(l), 0);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-content-secondary">
          Line items <span className="text-danger">*</span>
        </span>
        <Button type="button" variant="outline" size="sm" onClick={addRow}>
          <Plus className="h-3.5 w-3.5" /> Add row
        </Button>
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-card-hover text-xs uppercase text-content-muted">
            <tr>
              <th className="w-[38%] px-2 py-2 text-left">Product</th>
              <th className="w-[12%] px-2 py-2 text-right">Qty</th>
              <th className="w-[16%] px-2 py-2 text-right">Unit price (₹)</th>
              <th className="w-[12%] px-2 py-2 text-right">Tax %</th>
              <th className="w-[16%] px-2 py-2 text-right">Line total (₹)</th>
              <th className="w-[6%] px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {value.map((row, idx) => (
              <tr key={row.key} className="border-t border-border align-top">
                <td className="px-2 py-1.5">
                  <SearchableSelect
                    value={row.productId}
                    onChange={(v) => patchRow(idx, { productId: v })}
                    options={productOptions}
                    loading={productOptionsLoading}
                    placement="auto"
                    placeholder="Pick a product"
                    searchPlaceholder="Search products…"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <NumberField
                    min={0}
                    step="1"
                    value={row.quantity}
                    onValueChange={(n) => patchRow(idx, { quantity: n })}
                    className="h-9 w-full rounded-md border border-border bg-surface-card px-2 text-right text-sm outline-none focus:border-primary"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <NumberField
                    min={0}
                    step="0.01"
                    value={row.unitPrice}
                    onValueChange={(n) => patchRow(idx, { unitPrice: n })}
                    className="h-9 w-full rounded-md border border-border bg-surface-card px-2 text-right text-sm outline-none focus:border-primary"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <NumberField
                    min={0}
                    step="0.01"
                    value={row.taxRate}
                    onValueChange={(n) => patchRow(idx, { taxRate: n })}
                    className="h-9 w-full rounded-md border border-border bg-surface-card px-2 text-right text-sm outline-none focus:border-primary"
                  />
                </td>
                <td className="px-2 py-1.5 text-right text-sm text-content-primary">
                  {computeLineTotal(row).toFixed(2)}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded text-danger hover:bg-surface-card-hover"
                    onClick={() => removeRow(idx)}
                    title="Remove row"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border bg-surface-card-hover">
              <td className="px-2 py-2 text-right text-xs font-medium text-content-secondary" colSpan={4}>
                Grand total
              </td>
              <td className="px-2 py-2 text-right text-sm font-semibold text-content-primary">
                ₹ {(Math.round(grandTotal * 100) / 100).toFixed(2)}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/**
 * Lightweight customer-history preview shown under the Customer
 * picker. Fetches the three most-recent orders for the picked
 * `customerId` and surfaces their order number / date / total /
 * status. Renders nothing until a customer is actually picked, so
 * an empty initial form stays clean.
 *
 * Read-only by design — when Backend Item 3 ships and we have a
 * proper saved-address book, the same panel can grow a one-click
 * "Use last shipping address" affordance.
 */
function CustomerOrdersHint({ customerId }: { customerId: string }) {
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!customerId) {
      setOrders([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const rows = await salesOrderService.filter({ customerId });
        if (cancelled) return;
        const sorted = [...(rows ?? [])].sort((a, b) => {
          const da = a.orderDate ?? a.createdAt ?? '';
          const db = b.orderDate ?? b.createdAt ?? '';
          return db.localeCompare(da);
        });
        setOrders(sorted.slice(0, 3));
      } catch {
        if (!cancelled) setOrders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  if (!customerId) return null;
  if (loading && orders.length === 0) {
    return (
      <Card className="p-3 text-xs text-content-muted">Loading recent orders…</Card>
    );
  }
  if (orders.length === 0) {
    return (
      <Card className="p-3 text-xs text-content-muted">
        No previous orders for this customer.
      </Card>
    );
  }
  return (
    <Card className="p-0">
      <div className="border-b border-border px-3 py-2 text-xs font-semibold text-content-secondary">
        Recent orders for this customer
      </div>
      <ul className="divide-y divide-border">
        {orders.map((o) => {
          const id = String(o.salesOrderId ?? o.orderNumber ?? '');
          const date = (o.orderDate ?? o.createdAt ?? '').slice(0, 10);
          const statusInt = typeof o.status === 'number' ? o.status : Number(o.status);
          const statusLabel =
            Number.isFinite(statusInt) && (LABEL_BY_STATUS as Record<number, string>)[statusInt]
              ? (LABEL_BY_STATUS as Record<number, string>)[statusInt]
              : 'Pending';
          return (
            <li
              key={id}
              className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
            >
              <div className="min-w-0 flex-1 truncate font-medium text-content-primary">
                {o.orderNumber ?? id}
              </div>
              <div className="text-content-muted">{date || '—'}</div>
              <div className="font-semibold text-content-primary">
                ₹ {Number(o.totalAmount ?? 0).toLocaleString('en-IN')}
              </div>
              <StatusBadge status={statusLabel} />
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
